/**
 * Client half of the two SIGN-IN HOST GATES. Both refuse a sign-in whose credentials were
 * CORRECT but which arrived on the wrong hostname, and both use the same wire shape:
 *
 *  - PILOT-LOGIN-COM-ONLY (inner, role-scoped): a `MidasPilot` outside
 *    `PilotAccess:AllowedSignInHosts` =>
 *    `403 { errorCode: "PILOT_HOST_NOT_ALLOWED", details: { type: "pilot_host", allowedHost } }`.
 *  - HOST-POLICY-TENANT-HOME (outer, tenant-scoped): any user on a surface that is not their
 *    tenant's home =>
 *    `403 { errorCode: "HOST_NOT_ALLOWED", details: { type: "tenant_home_host", allowedHost } }`.
 *
 * The UI treats them IDENTICALLY — same calm notice, same copy, same link — because to the user
 * they are the same fact ("you are on the wrong address, here is yours"). Only the server knows,
 * and needs to know, which rule refused. This module holds the wire constants, a hard validation
 * of the host before it is ever put into a link, and a one-shot hand-off across the forced
 * logout+redirect that a non-login 403 triggers.
 *
 * SECURITY (SEC-REQ-PH-6):
 *  - `allowedHost` is a server-CONFIGURED value, never echoed request data — but it is
 *    validated here anyway before being interpolated into an `href`, so a future server
 *    change can never turn this notice into an open redirect / javascript: sink.
 *  - We NEVER auto-redirect to the allowed host. The user clicks the link, and no token
 *    travels with it (the session was never created).
 */

/** Inner, role-scoped gate (PILOT-LOGIN-COM-ONLY). Wire contract — do not rename. */
export const PILOT_HOST_NOT_ALLOWED = "PILOT_HOST_NOT_ALLOWED";

/** Outer, tenant-scoped gate (HOST-POLICY-TENANT-HOME). Wire contract — do not rename. */
export const HOST_NOT_ALLOWED = "HOST_NOT_ALLOWED";

/**
 * The 403 error codes that are NOT permission denials and must therefore skip the global
 * `handle403Error` toast/redirect. Adding a code here is the ONLY thing needed to give a new
 * host gate the same UX.
 */
export const HOST_GATE_ERROR_CODES = new Set<string>([PILOT_HOST_NOT_ALLOWED, HOST_NOT_ALLOWED]);

export type HostGateErrorCode = typeof PILOT_HOST_NOT_ALLOWED | typeof HOST_NOT_ALLOWED;

export const isHostGateErrorCode = (code?: string): boolean =>
  typeof code === "string" && HOST_GATE_ERROR_CODES.has(code);

const STORAGE_KEY = "pilotHostGate";

/** Bare hostname only: lowercase letters, digits, dots and hyphens. No scheme, no port, no path. */
const SAFE_HOST = /^[a-z0-9.-]+$/;

export interface PilotHostGateState {
  /** Canonical host to sign in on, or undefined when the server could not name one. */
  allowedHost?: string;

  /**
   * Which gate refused — `PILOT_HOST_NOT_ALLOWED` or `HOST_NOT_ALLOWED`. Recorded for
   * diagnosis; the rendered copy is deliberately identical for both. Undefined for a notice
   * picked up from the stash (see `stashPilotHostGate`).
   */
  code?: HostGateErrorCode;
}

export const isSafeHost = (host?: string): boolean =>
  typeof host === "string" && host.length > 0 && host.length <= 253 && SAFE_HOST.test(host);

/** Returns the host only when it is safe to build a link from. */
export const sanitiseAllowedHost = (host?: string): string | undefined =>
  isSafeHost(host) ? host : undefined;

/** `https://<host>/account/sign-in` — only ever called with a sanitised host. */
export const buildPilotSignInUrl = (host: string): string =>
  `https://${host}/account/sign-in`;

/**
 * Stash the notice across the logout + full-page redirect that a non-login host-gate 403
 * (i.e. an already-held session, or a token refresh) forces.
 * sessionStorage, not localStorage: it must not outlive the tab.
 *
 * Carries the HOST ONLY, for both gates: the two produce identical copy, so the code would be
 * dead weight in storage, and the pilot ticket's stash format is left byte-identical.
 */
export const stashPilotHostGate = (allowedHost?: string): void => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, sanitiseAllowedHost(allowedHost) || "");
  } catch {
    // Private mode / storage disabled — the user still lands on the sign-in page and
    // will see the notice on their next sign-in attempt. Never block the logout.
  }
};

/** Reads AND clears the stash — the notice is shown once, on the page we redirected to. */
export const consumePilotHostGate = (): PilotHostGateState | undefined => {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      return undefined;
    }

    window.sessionStorage.removeItem(STORAGE_KEY);
    return { allowedHost: sanitiseAllowedHost(stored) };
  } catch {
    return undefined;
  }
};

/**
 * COM-REGISTRATION-INVITE-ONLY — the REGISTRATION surface refusal (HOST_POLICY_SPEC §7).
 * `403 { code: 1012, errorCode: "REGISTRATION_NOT_AVAILABLE_ON_HOST" }`, thrown by the CUSTOMERS
 * service when company self-registration is attempted off the `.cc` surface.
 *
 * DELIBERATELY NOT a member of `HOST_GATE_ERROR_CODES`: those two are SIGN-IN gates, and membership
 * of that set also routes an error through the stash + forced-logout + redirect-to-sign-in branch in
 * `core/api.ts`. This one must instead land on the invite-only page, and there is no session to end.
 * It only shares ONE behaviour with them — it must skip the generic `handle403Error` toast.
 *
 * Wire contract — do not rename. Mirrors `CustomersErrors.REGISTRATION_NOT_AVAILABLE_ON_HOST_ERROR_CODE`.
 */
export const REGISTRATION_NOT_AVAILABLE_ON_HOST = "REGISTRATION_NOT_AVAILABLE_ON_HOST";

/** True when a serialized/interceptor error carries the registration-surface refusal code. */
export const isRegistrationHostRefusal = (code?: string): boolean =>
  code === REGISTRATION_NOT_AVAILABLE_ON_HOST;
